/* jshint esversion: 6 */
import {Exceptions} from "common/Exceptions";

/**
 * Builds an exception message
 *
 * @param {String} exceptionName The name of the exception
 * @param {Object} expression The expression for the assertion
 * @param {String} detailedMessage A detailed message text
 *
 * @returns {String}
 */
function buildMessage(exceptionName, expression, detailedMessage) {

    let errorMessage = `${exceptionName}\n`;

    if (typeof (detailedMessage) !== "undefined" && detailedMessage) {
        errorMessage += `Message: ${detailedMessage}\n`;
    }

    if (typeof (expression) !== "undefined" && expression) {
        errorMessage += `Expression: ${expression.toString()}`;
    }

    return errorMessage;
}

function throwError(message) {
    throw new Error(message);
}

function isNull(argument) {

    if (typeof (argument) === "undefined") {
        return true;
    }

    if (typeof (argument) === "number") {
        return false;
    }

    if (typeof (argument) === "boolean") {
        return false;
    }

    return argument === null;
}

export const Assert = {

    /**
     * Checks an argument and throws an error if the argument is not defined or is solved to <b>null</b>
     * Also throws an error for an empty string
     *
     * @param {Object} argument The argument for the assertion
     * @param {String} [message] An error message
     */
    checkArgument: function (argument, message) {
        if (!argument && typeof (argument) !== "number" && typeof (argument) !== "boolean") {
            throwError(buildMessage(Exceptions.IllegalArgument, argument, message));
        }
    },

    /**
     * Checks an argument to be of a specific type and throws an error if the argument is not defined or is solved to <b>null</b>
     * Also throws an error for an empty string
     *
     * @param {Object} argument The argument for the assertion
     * @param {String} type The type name
     * @param {String} [message] An error message
     */
    checkArgumentType: function (argument, type, message) {
        if (typeof (argument) !== type) {
            throwError(buildMessage(Exceptions.IllegalArgument, argument, message));
        }
    },

    /**
     * Checks a state and throws an error if the expression is <b>false</b> or <b>null</b>
     *
     * @param {Object} state The object for the assertion
     * @param {String} [message] An error message
     */
    checkState: function (state, message) {
        if (!state) {
            throwError(buildMessage(Exceptions.IllegalState, state, message));
        }
    },

    /**
     * Checks the given object for the type <b>Function</b>
     *
     * @param {Object} fn The object for the assertion
     * @param {String} [message] An error message
     */
    checkArgumentIsFunction: function (fn, message) {
        if (typeof fn !== "function") {
            throwError(buildMessage(Exceptions.IllegalState, "object is not a function!", message));
        }
    },

    /**
     * Checks the given object for the type <b>String</b>
     *
     * @param {Object} value The object for the assertion
     * @param {String} [message] An error message
     */
    checkArgumentIsString: function (value, message) {
        if (typeof value !== "string") {
            throwError(buildMessage(Exceptions.IllegalState, "object is not a String!", message));
        }
    },

    /**
     * Checks the given name is a valid url param with value
     *
     * @param {String} name The param name
     * @param {String} [message] An error message
     */
    checkUrlArgument: function (name, message) {
        const url = new URL( location.href );
        const value = url.searchParams.get( name );
        if (!value) {
            throwError(buildMessage(Exceptions.IllegalArgument, name, message));
        }
    },
    /**
     * Checks an index of an array state and throws an error if the index is out of bounds
     *
     * @param {Number} index The element index
     * @param {Number} size The size or length of the array
     * @param {String} [message] An error message
     */
    checkElementIndex: function (index, size, message) {
        if (typeof (index) === "undefined" || index < 0 || index >= size) {
            const expression = `index=${index}, size=${size}`;
            throwError(buildMessage(Exceptions.IndexOutOfBounds, expression, message));
        }
    },

    /**
     * Checks an object for expected children and throws a "ChildNotAvailable" exception
     * if the given object is not containing a child from the list.
     *
     * @param {Object} objectToCheck The object to check
     * @param {Array} names The names of the children, that the object should provide
     * @param {String} [message] An error message
     */
    checkForExpectedChildren: function (objectToCheck, names, message) {

        let childName,
            i;

        for (i = 0; i < names.length; i++) {
            childName = names[i];
            if (typeof (objectToCheck[childName]) === "undefined") {
                throwError(buildMessage(Exceptions.ChildNotAvailable, childName, message));
            }
        }
    },

    /**
     * Checks an object for expected children and throws a "ChildNotAvailable" exception
     * if the given object is not containing a child or containing the child with a null value.
     *
     * @param {Object} objectToCheck The object to check
     * @param {Array} names The names of the children, that the object should provide
     * @param {String} [message] An error message
     */
    checkForExpectedNotNullChildren: function (objectToCheck, names, message) {

        let childName,
            i;

        for (i = 0; i < names.length; i++) {
            childName = names[i];
            if (isNull(objectToCheck[childName])) {
                throwError(buildMessage(Exceptions.ChildNotAvailable, childName, message));
            }
        }
    },

    /**
     * Checks an expression and throws an error if the expression is <b>false</b> or <b>null</b>
     *
     * @param {Object} expression The expression for the assertion
     * @param {String} [message] An error message
     */
    assert: function (expression, message) {
        if (!expression) {
            throwError(message || (`${expression} does not equal true`));
        }
    },

    /**
     * Checks if a numerical value is in a range of two arguments.
     * An error will be thrown if the value is not in the specified range.
     *
     * @param {Number} value The value for the assertion
     * @param {Number} min The lowest possible number
     * @param {Number} max The highest possible number
     * @param {String} [message] An error message
     */
    inRange: function (value, min, max, message) {
        if (typeof (value) !== "number" || value < min || value > max) {
            const expression = `value=${value}, min=${min}, max=${max}`;
            throwError(buildMessage(Exceptions.OutOfRange, expression, message));
        }
    }
};
